import React, { useContext, useEffect, useState } from 'react';
import { Container, Card, CardHeader, CardBody } from 'reactstrap';
import styles from './ClaimSummary.module.css';
import moment from 'moment';
import { Spinner } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import { SessionContext } from '../../context/SessionContext';

function statusFormatter(column, colIndex) {
    return (
        <span>
            <span
                className={`circleBadge ${column === 'Open' ? 'success' : column === 'Saved' ? 'warning' : 'danger'}`}
            ></span>
            {column}
        </span>
    );
}

function headerFormatter(column, index, { sortElement, filterElement }) {
    const { order } = sortElement.props;
    return (
        <span className={`${order === 'asc' ? styles.downArrow : ''} ${order === 'desc' ? styles.upArrow : ''}`}>
            {column.text}
        </span>
    );
}

export const ClaimSummary = ({ contractId, clientId }) => {
    const { tabDispatch, getCurrentTabAndSubtab, getProfile } = useContext(SessionContext);
    const { tab } = getCurrentTabAndSubtab();
    const [claims, setClaims] = useState(tab.contractClaims || null);
    const [errorMessage, setErrorMessage] = useState();
    const [defaultSortedBy, setDefaultSortedBy] = useState([
        {
            dataField: 'claimSetupDate',
            order: 'desc'
        }
    ]);

    useEffect(() => {
        const { contractClaims } = tab;
        // if contract claims have been looked up already, insert previous result while checking for latest
        if (contractClaims) {
            setClaims(contractClaims);
            fetchClaims();
            return;
        }

        // if tab doesn't have any, fetch them
        fetchClaims();
    }, [contractId]);

    function dateFormatter(column, colIndex) {
        const profile = getProfile();
        const dateFormat = profile?.country?.toLowerCase() === 'canada' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
        return column ? moment(column).format(dateFormat) : '';
    }

    const fetchClaims = async () => {
        try {
            const response = await fetch(`/claim/GetClaimsByContractId/${contractId}/hvac`);
            const claimsResponse = await response.json();
            const updatedClaimsResponse = claimsResponse.map((claim) => ({
                ...claim,
                assetName: claim?.assetModels
            }));

            tabDispatch({
                type: 'UPDATE_TAB',
                payload: {
                    tabId: tab.tabId,
                    contractClaims: updatedClaimsResponse
                }
            });
            setClaims(updatedClaimsResponse ? updatedClaimsResponse : []);
        } catch (error) {
            setErrorMessage('Error fetching claims. Try again.');
        }
    };

    const claimNumberFormatter = (cell, row) => (
        <a
            href='#'
            className='btnLink'
        >
            {cell}
        </a>
    );

    const rowEvents = {
        onClick: (e, row, rowIndex) => {
            handleClaimNumberClick(row);
        }
    };

    const handleClaimNumberClick = (tableData) => {
        const { subtab } = getCurrentTabAndSubtab();
        tabDispatch('ADD_SUBTAB', {
            subtabType: 'staticclaiminvoice',
            invoiceId: subtab?.invoiceId,
            claimNumber: tableData?.claimNumber
        });
    };

    const handleSorting = (field, order) => {
        setDefaultSortedBy([
            {
                dataField: field,
                order: order
            }
        ]);
    };

    const columns = [
        {
            dataField: 'claimNumber',
            text: 'Claim Number',
            formatter: claimNumberFormatter,
            sort: true,
            onSort: handleSorting,
            headerFormatter: headerFormatter
        },
        {
            dataField: 'assetName',
            text: 'Asset',
            sort: true,
            onSort: handleSorting,
            formatter: (cell, row) => {
                if (Array.isArray(cell) && cell.length > 0) {
                    const assetnames = cell.map((item) => item.assetName).join(', ');
                    return <span title={assetnames}>{assetnames}</span>;
                }
                return '';
            },
            headerFormatter: headerFormatter,
            sortFunc: (rowone, rowtwo, order) => {
                const firstAssetName = rowone.map((item) => item.assetName).join(' ');
                const secondAssetName = rowtwo.map((item) => item.assetName).join(' ');
                if (order === 'desc') return secondAssetName.localeCompare(firstAssetName);
                else if (order === 'asc') return firstAssetName.localeCompare(secondAssetName);
            }
        },
        {
            dataField: 'claimStatusName',
            text: 'Claim Status',
            formatter: statusFormatter,
            sort: true,
            onSort: handleSorting,
            headerFormatter: headerFormatter
        },
        {
            dataField: 'claimSetupDate',
            text: 'Date Opened',
            formatter: dateFormatter,
            sort: true,
            onSort: handleSorting,
            headerFormatter: headerFormatter
        },
        {
            dataField: 'claimDecisionCompletedDate',
            text: 'Date Closed',
            formatter: dateFormatter,
            sort: true,
            onSort: handleSorting,
            headerFormatter: headerFormatter
        }
    ];

    // TODO: RETURN PAGELOADING COMPONENT
    // RETURN SPINNER UNTIL CLAIMS RESOLVES --OR-- ERROR MESSAGE
    if (!claims) {
        return (
            <Container className='pageLoading'>
                {errorMessage ? (
                    <div className='centeredXY text-center'>
                        <p>{errorMessage}</p>
                    </div>
                ) : (
                    <div className='centeredXY text-center'>
                        <Spinner
                            animation='border'
                            variant='primary'
                            role='status'
                        />
                        <p>Loading Claim Summary</p>
                    </div>
                )}
            </Container>
        );
    }

    // RETURN CLAIMS TABLE WHEN NOT LOADING
    return (
        <Container className='px-0'>
            <Card>
                <CardHeader>Claims Summary</CardHeader>
                <CardBody>
                    <div className='table-responsive'>
                        <BootstrapTable
                            bootstrap4
                            id='recTable'
                            classes={styles.claimSummaryTable}
                            defaultSorted={defaultSortedBy}
                            keyField='claimId'
                            data={claims || []}
                            columns={columns}
                            bordered={false}
                            hover
                            rowEvents={rowEvents}
                        />
                    </div>
                </CardBody>
            </Card>
        </Container>
    );
};
